/**
 * @file todoGroupingApi.ts
 * @brief To-do grouping calls (F1a, ADR-062): the learned category list,
 *        rename, merge, move one to-do, rename a topic. Every change
 *        refetches the meeting and My to-dos.
 *
 * @references docs/product/todo-grouping.md, BACKEND todoGrouping.routes.mjs
 * @author DES (prepared with Claude)
 * @date 31-10-2026
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet, apiPatch, apiPost } from "@/shared/api/client";
import { boardroomEndpoints } from "@/shared/api/endpoints";
import { companyMeetingsKeys, meetingDetailKeys } from "@/features/company-meetings/api";
import { myTodosKeys } from "./myTodosApi";

const CategoryListSchema = z
  .object({
    scope: z.string().catch("personal"),
    canManage: z.boolean().catch(false),
    categories: z
      .array(z.object({ id: z.string(), name: z.string(), createdBy: z.string().nullable().optional() }).passthrough())
      .catch([]),
  })
  .passthrough();
export type TodoCategoryList = z.infer<typeof CategoryListSchema>;
export type TodoCategory = TodoCategoryList["categories"][number];

export const todoCategoryKeys = {
  all: ["todo-categories"] as const,
  list: (meetingId: string | null) => ["todo-categories", meetingId ?? "mine"] as const,
};

/** The list a meeting's to-dos belong to (null = the viewer's own / company list). */
export function useTodoCategories(meetingId: string | null, enabled = true) {
  return useQuery({
    queryKey: todoCategoryKeys.list(meetingId),
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const qs = meetingId ? `?meetingId=${encodeURIComponent(meetingId)}` : "";
      return CategoryListSchema.parse(await apiGet<unknown>(`${boardroomEndpoints.todoCategories}${qs}`));
    },
  });
}

function refetchAll(qc: QueryClient, meetingId?: string | null) {
  void qc.invalidateQueries({ queryKey: todoCategoryKeys.all });
  void qc.invalidateQueries({ queryKey: myTodosKeys.all });
  if (meetingId) {
    for (const key of meetingDetailKeys(meetingId)) void qc.invalidateQueries({ queryKey: key });
  } else {
    // A rename / merge touches every meeting in the list.
    void qc.invalidateQueries({ queryKey: companyMeetingsKeys.all });
  }
}

export function useRenameTodoCategory() {
  const qc = useQueryClient();
  return useMutation<{ merged?: boolean; category?: { id: string; name: string } }, Error, { categoryId: string; name: string }>({
    mutationFn: ({ categoryId, name }) => apiPatch(boardroomEndpoints.todoCategory(categoryId), { name }),
    onSuccess: () => refetchAll(qc),
  });
}

export function useMergeTodoCategories() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { fromId: string; intoId: string }>({
    mutationFn: (body) => apiPost(boardroomEndpoints.mergeTodoCategories, body),
    onSuccess: () => refetchAll(qc),
  });
}

export type MoveTodoVars = {
  meetingId: string;
  todoId: string;
  categoryId?: string;
  categoryName?: string;
  topic?: string;
};

export function useMoveTodo() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, MoveTodoVars>({
    mutationFn: ({ meetingId, todoId, ...body }) => apiPatch(boardroomEndpoints.todoGrouping(meetingId, todoId), body),
    onSuccess: (_d, { meetingId }) => refetchAll(qc, meetingId),
  });
}

export function useRenameTodoTopic() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { meetingId: string; from: string; to: string }>({
    mutationFn: ({ meetingId, from, to }) => apiPatch(boardroomEndpoints.todoTopics(meetingId), { from, to }),
    onSuccess: (_d, { meetingId }) => refetchAll(qc, meetingId),
  });
}
